function readSessionStoragePinValid(): boolean {
  try {
    const stored = sessionStorage.getItem('pin_session');
    if (!stored) return false;
    const parsed = JSON.parse(stored);
    return Boolean(parsed?.validUntil && Date.now() < parsed.validUntil);
  } catch {
    return false;
  }
}

type PinDeviceRef = { id?: number; pin_required?: boolean | null };

/**
 * Misma regla que el botón de un dispositivo: mapaDispositivos si ya cargó,
 * y PIN obligatorio mientras el mapa no está (no operar a ciegas).
 */
export function deviceRequiresPin(device: PinDeviceRef | null | undefined): boolean {
  if (!device || device.id == null) return false;
  const w = typeof window !== 'undefined' ? (window as any) : null;
  if (typeof w?.checkPinRequired === 'function') {
    return Boolean(w.checkPinRequired(device));
  }
  const mapa = w?.mapaDispositivos?.[device.id];
  const mapaLoaded = typeof w?.isMapaDispositivosCargado === 'function'
    ? Boolean(w.isMapaDispositivosCargado())
    : false;
  if (mapaLoaded && mapa) return Boolean(mapa.pin_required);
  const explicit = Boolean(mapa?.pin_required ?? device.pin_required);
  if (!mapaLoaded && !explicit) return true;
  return explicit;
}

/**
 * Gate de PIN para control Sonos/Samsung (y otros no-Shelly).
 * Reutiliza `window.requestPinForControl` + `executePendingControl` con action `resume`.
 * @returns true si se puede continuar ahora; false si se abrió el diálogo PIN.
 */
export function requirePinIfNeeded(
  pinRequired: boolean | undefined | null,
  deviceId: number,
  resume: () => void,
): boolean {
  if (!pinRequired) return true;
  // sessionStorage primero (setPinSession escribe sync); window.isPinSessionValid puede ir un tick atrasado.
  if (readSessionStoragePinValid()) return true;
  if (typeof window !== 'undefined' && (window as any).isPinSessionValid?.()) {
    return true;
  }
  if (typeof window !== 'undefined' && typeof (window as any).requestPinForControl === 'function') {
    (window as any).requestPinForControl(
      { id: deviceId, pin_required: true, camera_ids: [], __resume: resume },
      'resume',
    );
    return false;
  }
  return true;
}

/**
 * Si algún dispositivo del grupo tiene PIN, abre el mismo diálogo que el botón
 * individual y ejecuta `resume` recién cuando la sesión queda válida.
 * @returns true si se puede operar ahora; false si hay que esperar el PIN (o no se pudo pedirlo).
 */
export function requireGroupPinIfNeeded(
  devices: PinDeviceRef[],
  resume: () => void,
): boolean {
  const pinDevice = devices.find((device) => deviceRequiresPin(device));
  if (!pinDevice || pinDevice.id == null) return true;
  const allowedNow = requirePinIfNeeded(true, pinDevice.id, resume);
  if (!allowedNow) return false;
  if (readSessionStoragePinValid()) return true;
  if (typeof window !== 'undefined' && (window as any).isPinSessionValid?.()) return true;
  return false;
}
